import React, { useState, useEffect } from 'react';
import styles from '../styles/stepCommon.module.css';
import { STEP_UI } from '../config/uiStrings';
import { useLocale } from '../context/LocaleContext';

export interface ActionButton {
  text: string;
  action: () => void;
  variant?: 'primary' | 'secondary';
  external?: boolean;
}

export interface ActionItem {
  id: number;
  title: string;
  description: string;
  completed: boolean;
  hasButton?: boolean;
  buttonText?: string;
  buttonAction?: () => void;
  buttons?: ActionButton[];
  renderCustomDescription?: () => React.ReactNode;
  renderBelowButton?: () => React.ReactNode;
}

interface StepActionsProps {
  actions: ActionItem[];
  onActionChange: (updatedActions: ActionItem[]) => void;
  renderActionContent?: (action: ActionItem, index: number) => React.ReactNode;
  stepNumber?: number;
  canCompleteAction?: (actionId: number) => boolean;
  /** Override default sequential availability (e.g. Step Five actions 3–5 after action 2) */
  isActionAvailable?: (actionIndex: number, actions: ActionItem[]) => boolean;
}

const StepActions: React.FC<StepActionsProps> = ({
  actions,
  onActionChange,
  renderActionContent,
  stepNumber,
  canCompleteAction,
  isActionAvailable: isActionAvailableOverride,
}) => {
  useLocale(); // re-render when locale changes (STEP_UI getters use active locale)
  const [collapsedActions, setCollapsedActions] = useState<Set<number>>(new Set());

  useEffect(() => {
    // Auto-collapse all completed actions by default on mount or actions change
    const completedActionIds = actions.filter(action => action.completed).map(action => action.id);
    setCollapsedActions(new Set(completedActionIds));
  }, [actions]);

  const highestCompletedIndex = (() => {
    let idx = -1;
    actions.forEach((action, i) => {
      if (action.completed) idx = i;
    });
    return idx;
  })();

  const isActionAvailable = (actionIndex: number) => {
    if (isActionAvailableOverride) {
      return isActionAvailableOverride(actionIndex, actions);
    }
    if (actionIndex === 0) return true;
    return actions.slice(0, actionIndex).every(a => a.completed);
  };

  const handleCheckboxChange = (actionId: number) => {
    const updatedActions = actions.map(action =>
      action.id === actionId
        ? { ...action, completed: !action.completed }
        : action
    );
    onActionChange(updatedActions);
    // Collapse/expand logic
    const changedAction = updatedActions.find(a => a.id === actionId);
    if (changedAction?.completed) {
      setCollapsedActions(prev => {
        const next = new Set(Array.from(prev));
        next.add(actionId);
        return next;
      });
    } else {
      setCollapsedActions(prev => {
        const next = new Set(Array.from(prev));
        next.delete(actionId);
        return next;
      });
    }
  };

  const toggleActionCollapse = (actionId: number) => {
    setCollapsedActions(prev => {
      const next = new Set(Array.from(prev));
      if (next.has(actionId)) {
        next.delete(actionId);
      } else {
        next.add(actionId);
      }
      return next;
    });
  };

  return (
    <div className={styles.actionList}>
      {actions.map((action, index) => {
        const isAvailable = isActionAvailable(index);
        const isCollapsed = collapsedActions.has(action.id);
        // Allow unchecking (clearing completed) whenever the action is completed; only gate *completing* with canCompleteAction
        const canToggleCompleted = (isAvailable || (action.completed && index === highestCompletedIndex)) &&
          (action.completed || !canCompleteAction || canCompleteAction(action.id));

        // Collapsed state for completed actions
        if (action.completed && isCollapsed) {
          return (
            <div
              key={action.id}
              className={`${styles.actionItem} ${styles.completed} ${styles.collapsed}`}
              onClick={() => toggleActionCollapse(action.id)}
              data-action-id={action.id}
            >
              <div className={styles.actionHeader}>
                <div className={styles.actionNumber}>{index + 1}</div>
                <div className={styles.actionContent}>
                  <h3 className={styles.actionTitle}>✓ {action.title}</h3>
                </div>
                <div className={styles.collapseIndicator}>
                  <span>▶</span>
                  <span>Expand</span>
                </div>
              </div>
            </div>
          );
        }

        // Handle click to collapse on mobile (except on interactive elements)
        const handleActionClick = (e: React.MouseEvent<HTMLDivElement>) => {
          // Only collapse if the action is completed
          if (!action.completed) return;
          
          // Don't collapse if clicking on interactive elements
          const target = e.target as HTMLElement;
          const tagName = target.tagName.toLowerCase();
          const isInteractive = 
            tagName === 'button' || 
            tagName === 'input' || 
            tagName === 'textarea' || 
            tagName === 'select' || 
            tagName === 'a' ||
            target.closest('button') ||
            target.closest('input') ||
            target.closest('textarea') ||
            target.closest('select') ||
            target.closest('a');
          
          if (isInteractive) return;
          
          // Collapse the action
          toggleActionCollapse(action.id);
        };

        // Expanded state
        return (
          <div
            key={action.id}
            className={`${styles.actionItem} ${action.completed ? styles.completed : ''} ${action.completed ? styles.collapsible : ''}`}
            data-action-id={action.id}
            onClick={handleActionClick}
          >
            <div className={styles.actionHeader}>
              <div className={styles.actionNumber}>{index + 1}</div>
              <div className={styles.actionContent}>
                <h3 className={styles.actionTitle}>{action.title}</h3>
                {action.renderCustomDescription ? (
                  <div className={styles.actionDescription}>
                    {action.renderCustomDescription()}
                  </div>
                ) : (
                  <p className={styles.actionDescription}>{action.description}</p>
                )}
                {/* Render multiple buttons if available, otherwise fall back to single button */}
                {action.buttons && action.buttons.length > 0 ? (
                  <div className={styles.actionButtonsContainer}>
                    {action.buttons.map((button, buttonIndex) => (
                      <button
                        key={buttonIndex}
                        className={`${styles.actionButton} ${button.variant === 'secondary' ? styles.actionButtonSecondary : ''}`}
                        onClick={button.action}
                        {...(button.external && { target: '_blank', rel: 'noopener noreferrer' })}
                      >
                        {button.text}
                      </button>
                    ))}
                  </div>
                ) : action.hasButton && action.buttonText && action.buttonAction ? (
                  <button
                    className={styles.actionButton}
                    onClick={action.buttonAction}
                  >
                    {action.buttonText}
                  </button>
                ) : null}
                {action.renderBelowButton ? (
                  <div className={styles.actionBelowButton}>{action.renderBelowButton()}</div>
                ) : null}
              </div>
              <div className={styles.actionCheckbox}>
                <input
                  type="checkbox"
                  checked={action.completed}
                  onChange={() => handleCheckboxChange(action.id)}
                  disabled={!canToggleCompleted}
                  className={styles.checkbox}
                />
                <label className={styles.checkboxLabel}>{STEP_UI.COMPLETED}</label>
                {action.completed && (
                  <button
                    onClick={() => toggleActionCollapse(action.id)}
                    className={styles.collapseToggle}
                    title={STEP_UI.COLLAPSE}
                  >
                    {isCollapsed ? STEP_UI.EXPAND : STEP_UI.COLLAPSE}
                  </button>
                )}
              </div>
            </div>
            {renderActionContent && renderActionContent(action, index)}
          </div>
        );
      })}
    </div>
  );
};

export default StepActions; 